import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';

import { useAsyncDebounce } from 'react-table';

// MUI
import { useTheme, useMediaQuery, Grid, Box, Typography, Divider, Container, Stack } from '@mui/material';

// Custom
import PublicPage from '@/ui/PublicPage/PublicPage';
import { FormButton } from '@/ui/Form';
import PublicIzletCard from './PublicIzletCard';
import PublicIzletiFilters from './PublicIzletiFilters';
import api from '@/lib/api';

// Types
import { DCFile, DCRecord } from '@/@types/lib/dataController';
import { PickerItemValue } from '@/@types/models/model';

import LoaderContext from '@/components/LoaderContext/LoaderContext';
import gsService from '@/services/gsService';
import UserContext from '@/components/UserContext/UserContext';

const mapLayers = [
  'HGSS:OSM',
  'HGSS:zp_np',
  'HGSS:staze_ferrate',
  'HGSS:msp',
  'HGSS:staze_plan',
  'HGSS:zp_pp',
  'HGSS:zp_sr',
  'HGSS:staze_ostale',
  'HGSS:drzavna_granica',
  'HGSS:staze_ldt',
  'HGSS:staze_ldt_via_adriatica',
  'HGSS:staze_ldt_cldt',
  'HGSS:staze_ldt_via_dinarica',
  'HGSS:izlet_routes',
];

const PublicIzletiPage = () => {
  const { t } = useTranslation();
  const loaderContext = useContext(LoaderContext);
  const userContext = useContext(UserContext);
  const isLoggedIn = !!(userContext && userContext.id);

  const apiInstance = new api();
  const theme = useTheme();
  const isMobile: boolean = !useMediaQuery(theme.breakpoints.up('lg'));
  // const isVeryBig: boolean = useMediaQuery(theme.breakpoints.up('xxl'));

  const navigate = useNavigate();

  const activeTripsBGColor = '#E5EEF4';

  const [izletRecords, setIzletRecords] = useState<DCRecord[]>([]);

  const [filterState, setFilterState] = useState<DCRecord>({});
  const [limit, setLimit] = useState<number>(18);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const [firstFetchDone, setFirstFetchDone] = useState<boolean>(false);

  const observer = useRef<IntersectionObserver | null>(null);

  const handleFilterChange = (source: string, value: PickerItemValue[] | PickerItemValue | null) => {
    setFilterState((prev) => ({ ...prev, [source]: value }));
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // console.log('Last element reached — fetching more data');
          fetchIzleti(false);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchIzleti = useAsyncDebounce(
    (reset = false) => {
      if (loading || (!reset && !hasMore)) return;

      setLoading(true);
      loaderContext.toggleLoading(true);

      const params = new URLSearchParams();

      // Add filters only if they exist
      if (filterState.lok) params.append('lokacije', (filterState.lok as PickerItemValue[]).join(','));
      if (filterState.org) params.append('organizatori', (filterState.org as PickerItemValue[]).join(','));
      if (filterState.vrem_okvir) params.append('vrem_okvir', filterState.vrem_okvir as string);
      if (filterState.tip) params.append('tip', filterState.tip as string);
      if (filterState.vod) params.append('vodici', filterState.vod as string);

      // Add pagination
      params.append('limit', String(limit));
      params.append('offset', String(reset ? 0 : offset));

      const url = `${process.env.REACT_APP_APIPATH}public/izleti?${params.toString()}`;

      apiInstance
        ._get(url)
        .then((resp) => {
          if (resp.success) {
            const newRecords = resp.data
              ? (resp.data as DCRecord[]).map((rec) => {
                  return {
                    ...rec,
                    gsImgUrl: gsService.getImageUrl(mapLayers, rec.id as string, rec.bbox as [number, number, number, number], 400, 300),
                  };
                })
              : [];
            setIzletRecords((prev) => (reset ? newRecords : [...prev, ...newRecords]));
            setOffset((prev) => prev + limit);
            setHasMore(newRecords.length === limit); // If fewer records returned, no more data
          } else {
            // TODO: SNACKBAR?
          }
        })
        .catch((resp) => {
          console.log('error');
          // TODO: SNACKBAR?
        })
        .finally(() => {
          setLoading(false);
          loaderContext.toggleLoading(false);
          if (!firstFetchDone) {
            setFirstFetchDone(true);
          }
        });
    },
    firstFetchDone ? 1000 : 0
  ); // 1s debounce

  useEffect(() => {
    setOffset(0);
    fetchIzleti(true);
  }, [filterState]);

  return (
    <PublicPage overrideBgImage>
      <Box width='100%'>
        <Container maxWidth='xxl' style={{ padding: 0 }}>
          <Grid container p={isMobile ? 3 : 10}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Typography color='primary' variant='h4' component='h1'>
                {t('public.headings.safety_trips')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }} container mt={isMobile ? 2 : 0} columnGap='50px'>
              <Box textAlign='left' mt={isMobile ? 2 : 0} display='flex' flexDirection='column' gap={3}>
                <Typography variant="body1" color="primary" fontSize={isMobile ? '1.35rem' : '1.45rem'}>
                  {t('public.headings.safety_trips_subheader')}
                </Typography>
                <Typography variant="body1" fontSize={isMobile ? '1.1rem' : '1.2rem'}>
                  {t('public.paragraphs.izleti_1')}
                </Typography>
                <Typography variant="body1" fontSize={isMobile ? '1.1rem' : '1.2rem'}>
                  {t('public.paragraphs.izleti_2')}
                </Typography>
                <Box>
                  <FormButton
                    fullWidth={false}
                    variant="contained"
                    onClick={handleGoToLogin}
                    sx={
                      {
                        /* this removes margin hardcoded into FormButton */
                      }
                    }
                  >
                    {t(isLoggedIn ? 'buttons.go_to_private' : 'buttons.login_register')}
                  </FormButton>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Box display={"flex"} justifyContent={'end'} gap={4} sx={{flexDirection: {xs: "row", lg: "column"}, mt: {xs: 2, lg: 0}, alignItems: {xs: "start", lg: "end"}}}>
                <Box width='max-content' display='flex' gap={2}>
                  <img 
                    style={{ maxHeight: '110px' }} 
                    src="/images/VSHPS_logo.png" 
                    alt="Logo Vodička služba"/>
                  <img 
                    style={{ maxHeight: '150px' }} 
                    src="/images/hps_logo.png" 
                    alt="Logo HPS"/>
                </Box>
                <Stack width='230px' gap={2}>
                  <Typography textAlign="center" color='primary' variant="body2">{t('public.headings.mints')}</Typography>
                  <img src="/images/MINTS.png" 
                    width="100%" 
                    // style={{border: "2px solid white"}}
                    alt="Logo Ministarstvo turizma i sporta"
                    />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box width='100%' bgcolor={activeTripsBGColor}>
        <Container maxWidth='xxl' style={{ padding: 0 }}>
          <Grid container p={isMobile ? 3 : 10} py={isMobile ? 3 : 5}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Typography color='primary' variant='h4' component='h1'>
                {t('public.headings.active_safety_trips')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 9 }} container mt={isMobile ? 4 : undefined} justifyContent={isMobile ? 'center' : undefined}>
              <Grid size={12}>
                <PublicIzletiFilters onChangeFilter={handleFilterChange} />
              </Grid>
              <Grid size={12} py={isMobile ? 2 : 3}>
                <Divider sx={{ borderColor: 'primary.main' }} />
              </Grid>
              <Grid container size={12} spacing={2} position='relative'>
                {izletRecords.map((iz, index) => {
                  if (index === izletRecords.length - 1) {
                    return (
                      <Grid size={{ xs: 12, lg: 4 }} ref={lastElementRef} key={'public-card' + (iz.id as number)}>
                        <PublicIzletCard
                          gsImgUrl={iz.gsImgUrl as string}
                          id={iz.id as number}
                          prviDan={moment(iz.prvi_dan as string)}
                          img={iz.image as DCFile}
                          odrediste={iz.odrediste as string}
                          opis={iz.opis as string}
                          organizator={iz.organizator as string}
                        />
                      </Grid>
                    );
                  } else {
                    return (
                      <Grid size={{ xs: 12, lg: 4 }} key={'public-card' + (iz.id as number)}>
                        <PublicIzletCard
                          gsImgUrl={iz.gsImgUrl as string}
                          id={iz.id as number}
                          prviDan={moment(iz.prvi_dan as string)}
                          img={iz.image as DCFile}
                          odrediste={iz.odrediste as string}
                          opis={iz.opis as string}
                          organizator={iz.organizator as string}
                        />
                      </Grid>
                    );
                  }
                })}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box bgcolor={activeTripsBGColor} width='100%' flexGrow={1} />
    </PublicPage>
  );
};

export default PublicIzletiPage;
